import { useState } from 'react';
import { useValidators, useStakingInfo, useDelegations } from '@/hooks/useStaking';
import { useWallet } from '@/hooks/useWallet';
import { useChain } from '@cosmos-kit/react';
import { ValidatorList } from './ValidatorList';
import { MyDelegations } from './MyDelegations';
import { StakingModal } from './StakingModal';
import type { StakingAction } from './StakingModal';
import type { ValidatorInfo, StakingInfo, DelegationResponse, Balance, ValidatorRewardInfo } from '@/types/staking';
import { formatTokenAmount } from '@/utils/formatting';
import type { RedelegationPlan } from '@/utils/redelegation';
import { ConnectWalletPrompt } from '@/components/Wallet';
import { text } from '@/config/theme';

export function Staking(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'validators' | 'my-delegations'>('validators');
  const [selectedValidator, setSelectedValidator] = useState<ValidatorInfo | null>(null);
  const [selectedDelegation, setSelectedDelegation] = useState<DelegationResponse | null>(null);
  const [selectedValidators, setSelectedValidators] = useState<ValidatorInfo[]>([]);
  const [selectedValidatorRewards, setSelectedValidatorRewards] = useState<ValidatorRewardInfo | null>(null);
  const [totalRewards, setTotalRewards] = useState<Balance[] | null>(null);
  const [redelegationPlan, setRedelegationPlan] = useState<RedelegationPlan | null>(null);
  const [validatorMap, setValidatorMap] = useState<Map<string, ValidatorInfo> | null>(null);
  const [stakingAction, setStakingAction] = useState<StakingAction | null>(null);

  const { isConnected } = useWallet();
  const { address } = useChain('gravitybridge');
  const { data: validators, isLoading: validatorsLoading } = useValidators(3); // 3 = BOND_STATUS_BONDED
  const { data: stakingInfo } = useStakingInfo() as { data: StakingInfo | undefined };
  const { data: delegatorData } = useDelegations(address ?? undefined);

  // Get rewards for a specific validator
  const getRewardsForValidator = (validatorAddress: string): ValidatorRewardInfo | undefined => {
    const vr = delegatorData?.rewards?.rewards?.find(
      r => r.validator_address === validatorAddress
    );
    if (vr) {
      return { validator_address: vr.validator_address, rewards: vr.reward };
    }
    return undefined;
  };

  const handleDelegate = (validator: ValidatorInfo): void => {
    setSelectedValidator(validator);
    setSelectedDelegation(null);
    setStakingAction('delegate');
  };

  const handleUndelegate = (validator: ValidatorInfo, delegation: DelegationResponse): void => {
    setSelectedValidator(validator);
    setSelectedDelegation(delegation);
    setStakingAction('undelegate');
  };

  const handleRedelegate = (validator: ValidatorInfo, delegation: DelegationResponse): void => {
    setSelectedValidator(validator);
    setSelectedDelegation(delegation);
    setStakingAction('redelegate');
  };

  const handleClaimRewards = (validator: ValidatorInfo): void => {
    setSelectedValidator(validator);
    setSelectedDelegation(null);
    setSelectedValidatorRewards(getRewardsForValidator(validator.operator_address) ?? null);
    setStakingAction('claim');
  };

  const handleClaimAllRewards = (validatorList: ValidatorInfo[]): void => {
    setSelectedValidators(validatorList);
    // Use the pre-aggregated total from the backend
    setTotalRewards(delegatorData?.rewards?.total ?? []);
    setStakingAction('claim-all');
  };

  const handleRedelegateFromInactive = (plan: RedelegationPlan, valMap: Map<string, ValidatorInfo>): void => {
    setRedelegationPlan(plan);
    setValidatorMap(valMap);
    setStakingAction('redelegate-from-inactive');
  };

  const closeModal = (): void => {
    setSelectedValidator(null);
    setSelectedDelegation(null);
    setSelectedValidators([]);
    setSelectedValidatorRewards(null);
    setTotalRewards(null);
    setRedelegationPlan(null);
    setValidatorMap(null);
    setStakingAction(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className={`text-3xl font-bold mb-2 ${text.primary}`}>Staking</h1>
        {stakingInfo !== undefined && stakingInfo !== null && (
          <div className={`flex gap-6 text-sm ${text.secondary}`}>
            <div>
              <span className="font-medium">APR:</span> {stakingInfo.apr}
            </div>
            <div>
              <span className="font-medium">Total Staked:</span> {formatTokenAmount(String(stakingInfo.total_bonded))} GRAV
            </div>
            <div>
              <span className="font-medium">In Wallet:</span> {formatTokenAmount(String(stakingInfo.total_supply))} GRAV
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('validators')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'validators'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'validators' ? 'page' : undefined}
          >
            Validators
          </button>
          <button
            onClick={() => setActiveTab('my-delegations')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'my-delegations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'my-delegations' ? 'page' : undefined}
          >
            My Delegations
          </button>
        </nav>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'validators' && (
          <ValidatorList
            validators={validators ?? []}
            isLoading={validatorsLoading}
            onDelegate={handleDelegate}
          />
        )}
        {activeTab === 'my-delegations' && !isConnected && (
          <ConnectWalletPrompt actionDescription="view and manage your delegations" />
        )}
        {activeTab === 'my-delegations' && isConnected && (
          <MyDelegations
            onUndelegate={handleUndelegate}
            onRedelegate={handleRedelegate}
            onClaimRewards={handleClaimRewards}
            onClaimAllRewards={handleClaimAllRewards}
            onRedelegateFromInactive={handleRedelegateFromInactive}
          />
        )}
      </div>

      {/* Staking Modal */}
      {stakingAction && (
        (stakingAction === 'claim-all' && selectedValidators.length > 0) ||
        (stakingAction === 'redelegate-from-inactive' && redelegationPlan && redelegationPlan.operations.length > 0) ||
        (stakingAction !== 'claim-all' && stakingAction !== 'redelegate-from-inactive' && selectedValidator)
      ) && (
        <StakingModal
          validator={selectedValidator ?? undefined}
          validators={stakingAction === 'claim-all' ? selectedValidators : undefined}
          allActiveValidators={validators ?? []}
          delegation={selectedDelegation ?? undefined}
          validatorRewards={selectedValidatorRewards ?? undefined}
          totalRewards={totalRewards ?? undefined}
          redelegationPlan={stakingAction === 'redelegate-from-inactive' && redelegationPlan ? redelegationPlan : undefined}
          validatorMap={stakingAction === 'redelegate-from-inactive' && validatorMap ? validatorMap : undefined}
          action={stakingAction}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
